/**
 * Reexportação tipada de `shared/format.js` (a mesma formatação pt-BR usada pelo
 * backend no PDF) + alguns auxiliares só de interface. Nunca reimplemente estas
 * regras aqui — este arquivo só dá tipos ao que já existe em `shared/`.
 */
import * as shared from '@shared/format.js';

export const fmtBRL: (n: number) => string = shared.fmtBRL;
export const fmtPct: (n: number) => string = shared.fmtPct;
export const fmtNum: (n: number, decimals?: number) => string = shared.fmtNum;
export const fmtDateTime: (iso: string) => string = shared.fmtDateTime;
export const fmtDate: (iso: string | null | undefined) => string = shared.fmtDate;
export const maskCNPJ: (value: string) => string = shared.maskCNPJ;
export const maskCPF: (value: string) => string = shared.maskCPF;
export const maskPhone: (value: string) => string = shared.maskPhone;
export const isValidCNPJ: (value: string) => boolean = shared.isValidCNPJ;
export const isValidCPF: (value: string) => boolean = shared.isValidCPF;

/** Documento do cliente — CNPJ (pessoa jurídica) ou CPF (pessoa física). */
export const maskDocumento: (value: string) => string = shared.maskDocumento;
export const isValidDocumento: (value: string) => boolean = shared.isValidDocumento;
export const tipoDocumento: (value: string) => 'cpf' | 'cnpj' | null = shared.tipoDocumento;
export const labelDocumento: (value: string) => string = shared.labelDocumento;

/** Iniciais (até duas letras) para avatares textuais — ex.: "Bruno Alves" → "BA". */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Trunca texto em `max` caracteres, preservando palavras quando possível. */
export function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  const cut = text.slice(0, max).replace(/\s+\S*$/, '');
  return `${cut || text.slice(0, max)}…`;
}

/** Classe de cor sugerida para uma variação percentual (alta/queda de preço). */
export function deltaTone(deltaPct: number | null | undefined): 'positive' | 'attention' | 'neutral' {
  if (deltaPct === null || deltaPct === undefined || deltaPct === 0) return 'neutral';
  return deltaPct > 0 ? 'attention' : 'positive';
}

/** Formata uma variação percentual com sinal explícito — ex.: "+7,40%", "−2,00%". */
export function fmtDeltaPct(deltaPct: number): string {
  const sign = deltaPct > 0 ? '+' : deltaPct < 0 ? '−' : '';
  return `${sign}${fmtPct(Math.abs(deltaPct))}`;
}

/** Rótulos em pt-BR para o status de uma proposta. */
export const PROPOSAL_STATUS_LABEL: Record<string, string> = {
  rascunho: 'Rascunho',
  emitida: 'Emitida',
  aceita: 'Aceita',
  recusada: 'Recusada',
  cancelada: 'Cancelada',
};
